import { resolveSemanticColor, type SemanticColorKey } from '@/theme/semanticTokens';
import { type RadiusToken, type SpacingToken } from '@/theme/tokens';
import { r, s } from '@/theme/tokens';
import { Box } from './Box';
import { useTheme } from './hooks';
import type { ElementType, ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const RADIUS_MAP: Record<RadiusToken, string> = {
  none: r('none'),
  xs: r('xs'),
  sm: r('sm'),
  chip: r('chip'),
  md: r('md'),
  lg: r('lg'),
  card: r('card'),
  panel: r('panel'),
};

const resolveSpacing = (token: SpacingToken | null | undefined): string | undefined =>
  token ? SPACING_MAP[token] : undefined;

type FrameProps = {
  children: ReactNode;
  component?: ElementType;
  role?: string;
  tabIndex?: number;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  overflow?: 'visible' | 'hidden' | 'auto' | 'scroll';
  overflowX?: 'visible' | 'hidden' | 'auto' | 'scroll';
  overflowY?: 'visible' | 'hidden' | 'auto' | 'scroll';
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky' | 'static';
  display?: 'block' | 'flex' | 'grid' | 'inline-flex' | 'inline-block';
  cursor?: 'default' | 'pointer';
  opacity?: number;
  outline?: string;
  outlineOffset?: number;
  align?: 'stretch' | 'center' | 'flex-start' | 'flex-end' | 'baseline';
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
  padding?: SpacingToken | null;
  margin?: SpacingToken | null;
  radius?: RadiusToken;
  border?: boolean;
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  background?: SemanticColorKey;
  variant?: 'default' | 'code';
  gridColumn?: string;
  gridRow?: string;
  onClick?: () => void;
};

export const Frame = ({
  children,
  component,
  role,
  tabIndex,
  width,
  height,
  minWidth = 0,
  maxWidth,
  minHeight,
  maxHeight,
  overflow,
  overflowX,
  overflowY,
  position,
  display,
  cursor,
  opacity,
  outline,
  outlineOffset,
  align,
  justify,
  padding,
  margin,
  radius,
  border = false,
  borderTop,
  borderBottom,
  borderLeft,
  borderRight,
  background,
  variant = 'default',
  gridColumn,
  gridRow,
  onClick,
}: FrameProps): ReactElement => {
  const theme = useTheme();
  const resolvedBackground = background ? resolveSemanticColor(theme, background) : undefined;
  const codeStyles =
    variant === 'code'
      ? {
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          whiteSpace: 'pre-wrap',
        }
      : {};

  const resolvedComponent = component ?? 'div';

  const resolvedBorder = border ? `1px solid ${resolveSemanticColor(theme, 'border')}` : undefined;
  const resolvedRadius = radius ? RADIUS_MAP[radius] : undefined;

  return (
    <Box
      component={resolvedComponent}
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      sx={{
        width,
        height,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        overflow,
        overflowX,
        overflowY,
        position,
        display,
        cursor,
        opacity,
        outline,
        outlineOffset,
        alignItems: align,
        justifyContent: justify,
        p: resolveSpacing(padding),
        m: resolveSpacing(margin),
        borderRadius: resolvedRadius,
        border: resolvedBorder,
        borderTop,
        borderBottom,
        borderLeft,
        borderRight,
        backgroundColor: resolvedBackground,
        gridColumn,
        gridRow,
        ...codeStyles,
      }}
    >
      {children}
    </Box>
  );
};
